import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { MomentDateFormat } from '@s20/common/constants/momentFormats';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../Common';

const displayDateFormat = 'M/D/YYYY';

/**
 * Shows the pay period the server resolves for the picked date (the week or period the export or
 * recalculation will actually cover), so the picked date is never mistaken for the period start.
 * When the tenant's pay calendar is misconfigured the server answers 500 with a ServiceResponse whose
 * message names the setting to fix; that message is shown instead. A misconfiguration does not heal
 * on retry, so the query does not retry.
 */
export const ResolvedPayPeriodHint = ({ date }) => {
    const requestDate = date && moment(date).isValid() ? moment(date).format(MomentDateFormat.DateOnlyUTCHyphenated) : null;

    const { data: payPeriod, error } = useQuery({
        queryKey: ['payPeriod', requestDate],
        queryFn: () => httpClient.js(ApiRoutes.payroll.payPeriod(requestDate)),
        enabled: Boolean(requestDate),
        retry: false,
    });

    if (error) {
        return (
            <small
                className="form-text text-danger mb-3"
                data-testid="resolved-pay-period-error"
            >
                {error.handledError?.message ?? error.message}
            </small>
        );
    }

    if (!payPeriod) {
        return null;
    }

    return (
        <small
            className="form-text text-muted mb-3"
            data-testid="resolved-pay-period"
        >
            {`Covers ${moment(payPeriod.start).format(displayDateFormat)} - ${moment(payPeriod.end).format(displayDateFormat)}`}
        </small>
    );
};
